import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Plus, NotebookPen, ChevronRight, ChevronDown, Check } from "lucide-react";
import NewEventDialog from "@/components/NewEventDialog";
import { portalConsultaUrl } from "@/lib/tribunais";

import { SEL_CINZA } from "@/lib/selecao";
// Lote 101 — bloco "Acompanhamento" do detalhe do processo (revisão periódica, análises, providência,
// conferir na fonte). Lote 114 (aprovado em 24/10/2026) — novo formato a partir das imagens do Robson:
//  - bloco recolhido/expandido; não prioritário abre recolhido, prioritário abre expandido; abrir e
//    priorizar são ações independentes (a chave de prioridade fica no cabeçalho);
//  - "Registrar revisão" (mostra "Revisado em … · autor"), "Adicionar nota", "Reagendar revisão" (não conta
//    como análise; motivo vai ao histórico), periodicidade por extenso e "Personalizada (a cada N dias)";
//  - faixa de falha da consulta (sempre visível, mesmo recolhido) com "Tentar novamente";
//  - quadro "Próxima providência". O histórico de análises passou para o Histórico (filtro "Revisões").
const PERIODOS = [
  { value: "semanal", label: "Semanal" },
  { value: "quinzenal", label: "Quinzenal" },
  { value: "mensal", label: "Mensal" },
  { value: "personalizada", label: "Personalizada…" },
  { value: "nenhuma", label: "Sem revisão" },
];
const NAO_INFORMADO = "Não informado";
const brDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const brInstantDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric" });
};
const brInstantTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });
};
const ORIGEM = { datajud: "DataJud", djen: "DJEN", manual: "manual", colado: "colado", push: "PUSH" };
const TIPO_PROV = { audiencia: "Audiência", prazo: "Prazo", reuniao: "Compromisso", pericia: "Perícia" };
const isoLocalDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (n) => { const d = new Date(); d.setHours(12, 0, 0, 0); d.setDate(d.getDate() + n); return d; };
const SEMANA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

export default function AcompanhamentoProcesso({ processo, clients = [], onAtualizar, atualizando = false, reloadKey = 0, onChanged }) {
  const id = processo.processo_id;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const abriu = useRef(false);
  const [writing, setWriting] = useState(false);
  const [texto, setTexto] = useState("");
  const [provOpen, setProvOpen] = useState(false);
  const [pendente, setPendente] = useState(null);       // ação adiada por haver nota não salva
  const [custom, setCustom] = useState(null);            // número de dias digitado em "Personalizada"
  const [reagOpen, setReagOpen] = useState(false);
  const [reagEscolha, setReagEscolha] = useState(15);    // 7 | 15 | 30 | "data"
  const [reagData, setReagData] = useState("");
  const [reagMotivo, setReagMotivo] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await http.get(`/processos/${id}/acompanhamento`);
      setData(r.data);
      if (!abriu.current) { abriu.current = true; setOpen(!!r.data.prioritario); }
    } catch (e) { /* mantém o que já está na tela */ }
  }, [id]);
  useEffect(() => { load(); }, [load, reloadKey]);

  if (!data) return null;
  const rev = data.revisao || {};
  const prio = data.prioritario;
  const hoje = rev.vencida;
  const consulta = data.consulta || {};
  const temNotaSolta = writing && texto.trim().length > 0;
  const changed = () => { if (onChanged) onChanged(); };

  // Rascunho de nota não salva: ao recolher o bloco ou tirar a prioridade, pergunta antes de perder.
  const guardar = (fn) => {
    if (temNotaSolta) { setPendente(() => fn); return; }
    fn();
  };

  const toggleOpen = () => guardar(() => { setWriting(false); setOpen((o) => !o); });
  const setPrio = async (value) => {
    setBusy(true);
    try {
      await http.post(`/processos/${id}/prioritario`, { value });
      toast.success(value ? "Processo marcado como prioritário" : "Processo deixou de ser prioritário");
      if (value) setOpen(true);
      await load(); changed();
    } catch (e) { toast.error("Não consegui salvar. Tente de novo."); } finally { setBusy(false); }
  };
  const onSwitch = (value) => {
    if (!value) { guardar(() => { setWriting(false); setPrio(false); }); return; }
    setPrio(true);
  };
  const salvarPeriodo = async (periodo, dias) => {
    try {
      await http.post(`/processos/${id}/revisao/periodo`, dias ? { periodo, dias } : { periodo });
      toast.success("Revisão periódica atualizada");
      setCustom(null);
      await load(); changed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Não consegui salvar. Tente de novo."); }
  };
  const onPeriodo = (v) => {
    if (v === "personalizada") { setCustom(String(rev.periodo === "personalizada" && rev.dias ? rev.dias : 10)); return; }
    setCustom(null);
    salvarPeriodo(v);
  };
  const registrar = async (comTexto) => {
    setBusy(true);
    try {
      await http.post(`/processos/${id}/revisao`, comTexto ? { texto } : {});
      toast.success(comTexto && texto.trim() ? "Nota salva e revisão registrada" : "Revisão registrada");
      setTexto(""); setWriting(false);
      await load(); changed();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Não consegui registrar. Tente de novo."); return false; } finally { setBusy(false); }
  };
  const salvarEContinuar = async () => {
    if (await registrar(true)) { const f = pendente; setPendente(null); if (f) f(); }
  };
  const descartarEContinuar = () => { const f = pendente; setTexto(""); setWriting(false); setPendente(null); if (f) setTimeout(f, 0); };

  const quem = data.quem_revisa;
  const salvarModo = async (modo) => {
    setBusy(true);
    try {
      await http.post(`/processos/${id}/revisao/modo`, { modo });
      toast.success(modo === "escritorio" ? "Agora a revisão é do escritório" : "Agora a revisão é do responsável");
      await load(); changed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Não consegui salvar. Tente de novo."); await load(); } finally { setBusy(false); }
  };

  const reagDataFinal = reagEscolha === "data" ? reagData : isoLocalDay(addDays(Number(reagEscolha)));
  const reagDataObj = reagDataFinal ? new Date(`${reagDataFinal}T12:00:00`) : null;
  const abrirReagendar = () => { setReagEscolha(15); setReagData(""); setReagMotivo(""); setReagOpen(true); };
  const reagendar = async () => {
    setBusy(true);
    try {
      const body = reagEscolha === "data" ? { data: reagData } : { dias: Number(reagEscolha) };
      if (reagMotivo.trim()) body.motivo = reagMotivo.trim();
      await http.post(`/processos/${id}/revisao/reagendar`, body);
      toast.success("Revisão reagendada", { description: `Nova data: ${brDay(reagDataFinal)}` });
      setReagOpen(false);
      await load(); changed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Não consegui reagendar. Tente de novo."); } finally { setBusy(false); }
  };
  const conferirFonte = async () => {
    const url = portalConsultaUrl(data.tribunal_alias);
    try { await navigator.clipboard.writeText(data.numero || ""); } catch (e) { /* sem permissão de área de transferência */ }
    if (url) {
      toast.success("Número copiado", { description: "Cole no portal do tribunal que abriu." });
      window.open(url, "_blank", "noopener");
    } else {
      toast.success("Número copiado", { description: "Cole no portal do seu tribunal para conferir." });
    }
  };

  const mov = data.ultima_movimentacao;
  const ur = data.ultima_revisao;
  const prov = data.proxima_providencia;
  const proxTxt = !prio ? null
    : rev.periodo === "nenhuma" ? "Sem revisão periódica"
    : !rev.proxima ? NAO_INFORMADO
    : hoje ? (rev.proxima === isoLocalDay(new Date()) ? "Hoje — vence a revisão" : `Venceu em ${brDay(rev.proxima)}`)
    : brDay(rev.proxima);
  const periodoDesc = rev.periodo === "nenhuma" ? "sem revisão periódica"
    : rev.reagendada ? "data reagendada por você"
    : rev.dias ? `a cada ${rev.dias} dias${rev.periodo === "quinzenal" ? " (quinzenal)" : rev.periodo === "semanal" ? " (semanal)" : rev.periodo === "mensal" ? " (mensal)" : ""}` : "";
  const prefill = {
    event_type: "prazo", case_number: processo.numero || "", court: processo.vara || "",
    court_district: processo.comarca || "", party_author: processo.party_author || "",
    party_defendant: processo.party_defendant || "", client_id: processo.client_id || undefined,
  };
  const linha = "grid grid-cols-[7.5rem_1fr] sm:grid-cols-[9rem_1fr] gap-x-3 py-2";
  const rot = "text-[13px] text-stone-500 pt-0.5";
  const link = "text-[13px] text-stone-500 underline underline-offset-2 hover:text-stone-800";
  const linkIc = "inline-flex items-center gap-1 min-h-[36px] text-[13px] text-stone-500 hover:text-stone-800";

  const textoProv = prov ? `${prov.event_type === "prazo" ? (prov.deadline_type || "Prazo") : (prov.event_type === "reuniao" && prov.meeting_subject) || TIPO_PROV[prov.event_type] || "Compromisso"} — ${prov.event_type === "prazo" ? "prazo " : ""}${brDay(prov.date)}${prov.time ? ` às ${String(prov.time).slice(0, 5)}` : ""}` : "";

  return (
    <div className="space-y-3" data-testid="acomp-grupo">
    {/* Lote 168 — "Próxima providência" sai do bloco recolhível e fica sempre à vista, com "Criar providência" ao lado. */}
    <div className="bg-white border border-stone-200 rounded-md px-4 py-3.5 md:px-5" data-testid="acomp-providencia-box">
      <h2 className="font-display text-base font-bold text-stone-900">Próxima providência</h2>
      {prov ? (
        <p className="mt-1 flex flex-wrap items-baseline gap-x-3 text-sm text-stone-900">
          <span data-testid="acomp-prov-texto">{textoProv}</span>
          <button type="button" data-testid="acomp-prov-abrir" onClick={() => navigate(`/audiencia/${prov.pub_id}`)} className={link}>Abrir</button>
        </p>
      ) : (
        <p className="mt-1 text-sm text-stone-400" data-testid="acomp-prov-vazia">Nenhuma providência cadastrada</p>
      )}
      <button type="button" data-testid="acomp-providencia" onClick={() => setProvOpen(true)} className="mt-1.5 inline-flex items-center gap-1 min-h-[36px] text-sm font-semibold text-gold-800 underline underline-offset-2 hover:text-gold-900">
        <Plus className="w-3.5 h-3.5" strokeWidth={2.25} /> Criar providência
      </button>
    </div>
    <div className="bg-white border border-stone-200 rounded-md px-4 py-3 md:px-5 md:py-3.5" data-testid="acompanhamento">
      {/* Cabeçalho: abrir/fechar e prioridade são independentes */}
      <div className="flex items-center justify-between gap-3">
        <button type="button" data-testid="acomp-toggle" aria-expanded={open} onClick={toggleOpen}
          className="flex items-center gap-2 min-h-[44px] -ml-1 pl-1 pr-3 text-left rounded-md hover:bg-stone-50">
          {open ? <ChevronDown className="w-4 h-4 text-stone-500" strokeWidth={2} /> : <ChevronRight className="w-4 h-4 text-stone-500" strokeWidth={2} />}
          <h2 className="font-display text-sm font-bold tracking-widest uppercase text-stone-600">Acompanhamento</h2>
        </button>
        <label className="inline-flex items-center gap-1.5 text-[13px] sm:text-sm cursor-pointer select-none shrink-0" data-testid="acomp-star-label">
          <Star className={`w-4 h-4 ${prio ? "fill-gold-500 text-gold-500" : "text-stone-400"}`} strokeWidth={1.75} />
          <span className={prio ? "font-semibold text-gold-800" : "text-stone-500"}>Prioritário</span>
          <Switch data-testid="acomp-star" checked={prio} disabled={busy} onCheckedChange={onSwitch}
            aria-label="Prioritário" className="data-[state=checked]:bg-gold-500" />
        </label>
      </div>

      {/* Nota não salva: pergunta antes de recolher ou tirar a prioridade */}
      {pendente && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="acomp-pendente">
          <p className="text-amber-900 mb-2">Você tem uma nota que ainda não foi salva.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" data-testid="acomp-pend-salvar" disabled={busy} onClick={salvarEContinuar} className="bg-stone-900 hover:bg-stone-800 text-white">Salvar nota e continuar</Button>
            <Button type="button" size="sm" variant="outline" data-testid="acomp-pend-descartar" onClick={descartarEContinuar}>Descartar</Button>
            <Button type="button" size="sm" variant="ghost" data-testid="acomp-pend-voltar" onClick={() => setPendente(null)}>Voltar</Button>
          </div>
        </div>
      )}

      {/* Lote 146 (Robson, 30/10/2026): consulta frustrada não é anunciada em lugar nenhum; só a data da última consulta efetivada. */}

      {open && (
        <div className="mt-2" data-testid="acomp-corpo">
          {!prio && (
            <p className="text-xs text-stone-400 mb-1" data-testid="acomp-sem-prio">
              Marque como prioritário para receber alerta de cada movimentação e lembrete de revisão.
            </p>
          )}

          {/* Lote 146 (Robson, 30/10/2026): quadro enxuto, no mesmo desenho de "Dados do processo" — rótulo cinza à esquerda,
              valor à direita, linhas tracejadas; um único botão escuro e o resto como links discretos. */}
          <dl className="divide-y divide-dashed divide-stone-200" data-testid="acomp-cards">
            <div className={linha}>
              <dt className={rot}>Último movimento</dt>
              <dd className="min-w-0">
                <span className="text-sm font-medium text-stone-900" data-testid="acomp-mov">{mov ? brDay(mov.data) : NAO_INFORMADO}</span>
                <span className="block text-xs text-stone-400 truncate">
                  {mov ? `${ORIGEM[mov.origem] || ""}${mov.descricao ? `${ORIGEM[mov.origem] ? " · " : ""}${mov.descricao.slice(0, 60)}` : ""}` : "Ainda sem movimentações capturadas"}
                </span>
              </dd>
            </div>

            {prio && (
              <div className={linha}>
                <dt className={rot}>Revisão</dt>
                <dd className="min-w-0">
                  <span className="flex flex-wrap items-center gap-x-2">
                    <span className={`text-sm font-medium ${hoje ? "text-blue-700" : "text-stone-900"}`} data-testid="acomp-proxima">{proxTxt}</span>
                    <select data-testid="acomp-periodo" aria-label="Periodicidade da revisão" value={custom !== null ? "personalizada" : rev.periodo} onChange={(e) => onPeriodo(e.target.value)}
                      className="h-7 -ml-1 px-1 rounded-md bg-transparent text-xs text-stone-500 hover:text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-300">
                      {PERIODOS.map((p) => <option key={p.value} value={p.value}>{p.value === "personalizada" && rev.periodo === "personalizada" && rev.dias ? `A cada ${rev.dias} dias` : p.label}</option>)}
                    </select>
                    {rev.reagendada && rev.periodo !== "nenhuma" && <span className="text-xs text-stone-400">reagendada</span>}
                  </span>
                  <span className="block text-xs text-stone-400 truncate" data-testid="acomp-ultima">
                    {rev.ultima ? `última em ${brInstantDay(rev.ultima)} · por ${ur?.autor || NAO_INFORMADO}${ur?.texto ? ` — “${ur.texto.slice(0, 40)}${ur.texto.length > 40 ? "…" : ""}”` : ""}` : "nunca revisado"}
                  </span>
                  {quem?.em_escritorio && (
                    <span className="flex flex-wrap items-center gap-x-2 text-xs text-stone-500" data-testid="acomp-quem">
                      <span>Quem revisa:</span>
                      {quem.pode_mudar ? (
                        <select data-testid="acomp-quem-select" aria-label="Quem faz a revisão" disabled={busy} value={quem.modo} onChange={(e) => salvarModo(e.target.value)}
                          className="h-7 -ml-1 px-1 rounded-md bg-transparent text-xs text-stone-700 hover:text-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-300">
                          <option value="responsavel">{`Responsável${quem.responsavel_nome ? ` (${quem.responsavel_nome})` : ""}`}</option>
                          <option value="escritorio">Escritório (quem revisar primeiro dá baixa)</option>
                        </select>
                      ) : (
                        <span className="text-stone-700" data-testid="acomp-quem-texto">{quem.modo === "escritorio" ? "Escritório (quem revisar primeiro dá baixa)" : `Responsável${quem.responsavel_nome ? ` (${quem.responsavel_nome})` : ""}`}</span>
                      )}
                    </span>
                  )}
                </dd>
              </div>
            )}

            <div className={linha} data-testid="acomp-consulta-linha">
              <dt className={rot}>Última consulta</dt>
              <dd className="text-xs text-stone-400 self-center" data-testid="acomp-consulta">
                {consulta.ok_at ? `${brInstantDay(consulta.ok_at)} às ${brInstantTime(consulta.ok_at)}` : "ainda não consultada"}
              </dd>
            </div>
          </dl>

          {prio && custom !== null && (
            <div className="flex items-center gap-2 my-2 text-sm" data-testid="acomp-custom">
              <span className="text-stone-600">Revisar a cada</span>
              <Input data-testid="acomp-custom-dias" type="number" inputMode="numeric" min={1} max={365} value={custom}
                onChange={(e) => setCustom(e.target.value)} className="w-20 h-9" />
              <span className="text-stone-600">dias</span>
              <Button type="button" size="sm" data-testid="acomp-custom-ok" onClick={() => salvarPeriodo("personalizada", Number(custom))} className="bg-stone-900 hover:bg-stone-800 text-white">Salvar</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setCustom(null)}>Cancelar</Button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
            <Button type="button" size="sm" data-testid="acomp-revisado" disabled={busy} onClick={() => registrar(false)} className="bg-stone-900 hover:bg-stone-800 text-white gap-1.5 h-9">
              <Check className="w-4 h-4" strokeWidth={2} /> Registrar revisão
            </Button>
            <button type="button" data-testid="acomp-analise" onClick={() => setWriting((w) => !w)} className={linkIc}>
              <NotebookPen className="w-3.5 h-3.5" strokeWidth={1.75} /> Adicionar nota
            </button>
            {prio && rev.periodo !== "nenhuma" && (
              <button type="button" data-testid="acomp-reagendar" onClick={abrirReagendar} className={linkIc}>Reagendar revisão</button>
            )}
            <button type="button" data-testid="acomp-fonte" onClick={conferirFonte} className={linkIc}>Conferir na fonte</button>
          </div>

          {writing && (
            <div className="mt-2 space-y-2" data-testid="acomp-form">
              <Textarea
                data-testid="acomp-texto"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Nota (opcional) — ex.: Aguardando laudo; rever após 15/11."
              />
              <div className="flex items-center gap-2">
                <Button type="button" data-testid="acomp-salvar" disabled={busy} onClick={() => registrar(true)} className="bg-stone-900 hover:bg-stone-800 text-white">Salvar nota</Button>
                <span className="text-xs text-stone-500">Salva no histórico e já conta como revisão.</span>
              </div>
            </div>
          )}
        </div>
      )}

      <NewEventDialog
        hideTrigger
        open={provOpen}
        onOpenChange={setProvOpen}
        prefill={prefill}
        onCreated={() => { toast.success("Prazo agendado"); setProvOpen(false); load(); changed(); }}
      />

      <Dialog open={reagOpen} onOpenChange={setReagOpen}>
        <DialogContent data-testid="acomp-reag-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reagendar revisão</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">Hoje a próxima revisão é <strong>{rev.proxima ? brDay(rev.proxima) : NAO_INFORMADO}</strong>.</p>
            <div className="flex flex-wrap gap-2" data-testid="acomp-reag-opcoes">
              {[7, 15, 30].map((n) => (
                <button key={n} type="button" data-testid={`acomp-reag-${n}`} onClick={() => setReagEscolha(n)}
                  className={`h-10 px-4 rounded-full border text-sm font-semibold ${reagEscolha === n ? SEL_CINZA : "bg-white border-stone-300 text-stone-700"}`}>+{n} dias</button>
              ))}
              <button type="button" data-testid="acomp-reag-data" onClick={() => setReagEscolha("data")}
                className={`h-10 px-4 rounded-full border text-sm font-semibold ${reagEscolha === "data" ? SEL_CINZA : "bg-white border-stone-300 text-stone-700"}`}>Escolher data</button>
            </div>
            {reagEscolha === "data" && (
              <Input data-testid="acomp-reag-input" type="date" min={isoLocalDay(new Date())} value={reagData} onChange={(e) => setReagData(e.target.value)} className="w-48" />
            )}
            <p className="text-sm text-stone-700" data-testid="acomp-reag-nova">
              Nova data: <strong>{reagDataFinal ? brDay(reagDataFinal) : "—"}</strong>
              {reagDataObj ? ` (${SEMANA[reagDataObj.getDay()]})` : ""}
            </p>
            <Input data-testid="acomp-reag-motivo" value={reagMotivo} maxLength={300} onChange={(e) => setReagMotivo(e.target.value)} placeholder="Motivo (opcional): ex. aguardando laudo" />
            <p className="text-xs text-stone-500">Não conta como análise. Fica no histórico: data anterior, nova data, autor e motivo.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReagOpen(false)}>Cancelar</Button>
            <Button data-testid="acomp-reag-salvar" disabled={busy || !reagDataFinal} onClick={reagendar} className="bg-stone-900 hover:bg-stone-800 text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}
